import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './SetGuardian.css';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fireAudit } from '../utils/auditClient';

const NewPlayerGuardian = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedCenter } = useUser();
    const searchParams = new URLSearchParams(location.search);
    const userId = location.state?.userId || searchParams.get('userId');    
    const token = localStorage.getItem('auth-token');
    const [players, setPlayers] = useState([]);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [guardianForms, setGuardianForms] = useState([]);
    const baselineRef = useRef(null);

    useEffect(() => {
        const fetchGuardianData = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_GUARDIAN_DATA}?academy_id=${selectedCenter}&type=FAMILY&parentUser1=${userId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const data = await response.json();

                if (data.success && data.data.players) {
                    setPlayers(data.data.players.map(player => ({
                        id: player.id,
                        userId: player.user?.id,
                        name: player.user?.name || 'N/A',
                        mobile: player.user?.mobile || '-',
                    })));
                }

                if (data.success && data.data.guardianForm?.playerParentForms) {
                    let guardians = data.data.guardianForm.playerParentForms
                        .filter(guardian => guardian.name?.trim() || guardian.mobileNumber?.trim())
                        .map(guardian => ({
                            name: guardian.name?.trim() || '',
                            mobile: guardian.mobileNumber ? guardian.mobileNumber.replace(/^\+91/, '') : '',
                            relation: guardian.relation?.trim() || '',
                            userId: guardian.userId || '',
                            disabled: true,
                        }));

                    if (guardians.length === 1 && !data.data.guardianForm.parentUser2) {
                        guardians.push({
                            name: '',
                            mobile: '',
                            relation: '',
                            userId: '',
                            disabled: false,
                        });
                    }

                    setGuardianForms(guardians);
                    baselineRef.current = {
                        academyId: selectedCenter,
                        type: 'FAMILY',
                        parentUser1: userId,
                        selectedPlayerId: null,
                        playerParentForms: guardians.map(g => ({
                            name: g.name,
                            mobileNumber: g.mobile ? `+91${g.mobile}` : '',
                            relation: g.relation
                        }))
                    };
                } else {
                    const guardians = [{ name: '', mobile: '', relation: '', userId: '', disabled: false }];
                    setGuardianForms(guardians);
                    baselineRef.current = {
                        academyId: selectedCenter,
                        type: 'FAMILY',
                        parentUser1: userId,
                        selectedPlayerId: null,
                        playerParentForms: guardians.map(g => ({
                            name: g.name,
                            mobileNumber: g.mobile ? `+91${g.mobile}` : '',
                            relation: g.relation
                        }))
                    };
                }

                if (data?.data?.errorMessage && data?.data?.errorMessage.trim() === "Player Not found, please add a new one.") {
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: data?.data?.errorMessage,
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                    });
                    navigate('/setGuardianPlayer', {
                        state: {
                            academyId: Number(selectedCenter),
                            userId: userId,
                            guardianId: userId
                        }
                    });
                }
            } catch (error) {
            }
        };

        if (selectedCenter && userId) {
            fetchGuardianData();
        }
    }, [selectedCenter, userId, navigate]);

    const handleSubmit = async () => {
        if (!selectedPlayer) {
            alert("Please select a player before submitting.");
            return;
        }

        const selectedUserId = players.find(player => player.id === selectedPlayer)?.userId;
        if (!selectedUserId) {
            alert("User ID not found for the selected player.");
            return;
        }

        const requestBody = {
            userId: selectedUserId,
            academyId: selectedCenter,
            type: "FAMILY",
            playerParentForms: guardianForms.map(guardian => ({
                name: guardian.name,
                mobileNumber: guardian.mobile ? `+91${guardian.mobile}` : "",
                relation: guardian.relation
            }))
        };

        try {
            const response = await fetch(process.env.REACT_APP_API_ASSIGN_GTP, {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();

            if (data.success) {
                const oldPayload = baselineRef.current || {};
                const newPayload = { ...requestBody, selectedPlayerId: selectedPlayer };
                const affected_id = data?.data?.id ?? `${selectedCenter}-${selectedUserId}`;
                fireAudit({
                    entity: 'guardian_assignment',
                    affected_id,
                    affected_user:userId,
                    action: 'create',
                    section: 'Coaching',
                    subSection: 'Set Guardian',
                    businessApi: process.env.REACT_APP_API_ASSIGN_GTP,
                    businessMethod: 'POST',
                    oldPayload,
                    newPayload,
                    extra: { page: 'NewPlayerGuardian' },
                });
                baselineRef.current = newPayload;

                navigate("/createNewPlayer");
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Guardian details saved successfully!",
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                });
            } else {
                alert("Failed to save guardian details.");
            }
        } catch (error) {
            alert("An error occurred while saving the guardian details.");
        }
    };

    const handleCreateplayer = (event) => {
        const url = `/setGuardianPlayer?academyId=${selectedCenter}&userId=${selectedPlayer || userId}&guardianId=${userId}`;

        if (event?.metaKey || event?.ctrlKey) {
            window.open(url, "_blank"); 
        } else {
            navigate(url);
        }
    };

    return (
        <div className="enrollment-container">
            <h3>Select player and add relation</h3>
            <button
                className="create-button-new"
                onClick={handleCreateplayer}
            >
                <button
                    className="create-button-new"
                    onClick={(e) => handleCreateplayer(e)}
                >
                    <span className="create-button__icon">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="23"
                            height="23"
                            className="create-button__svg"
                        >
                            <path
                                d="M12 5v14M5 12h14"
                                stroke="white"
                                strokeWidth="2.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    
                    <span className="create-button__label">
                        Create Player
                    </span>
                </button>
            </button>
            <div className="sg-guardian-selection">
                <div className="sg-guardian-entry-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Select</th>
                                <th>Player Name</th>
                                <th>Mobile Number</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.length > 0 ? (
                                players.map((player) => (
                                    <tr key={player.id}>
                                        <td>
                                            <input
                                                type="radio"
                                                name="selectedPlayer"
                                                value={player.id}
                                                checked={selectedPlayer === player.id}
                                                onChange={() => setSelectedPlayer(player.id)}
                                            />
                                        </td>
                                        <td>{player.name}</td>
                                        <td>{player.mobile}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3">No players available.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="sg-guardian-selection">
                <div className="sg-guardian-entry-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Parent Number</th>
                                <th>Name</th>
                                <th>Relation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {guardianForms.length > 0 ? (
                                guardianForms.map((guardian, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="Number"
                                                value={guardian.mobile}
                                                onChange={(e) => {
                                                    if (!guardian.disabled) {
                                                        const updatedForms = [...guardianForms];
                                                        updatedForms[index].mobile = e.target.value;
                                                        setGuardianForms(updatedForms);
                                                    }
                                                }}
                                                maxLength={10}
                                                disabled={guardian.disabled}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={guardian.name}
                                                onChange={(e) => {
                                                    if (!guardian.disabled) {
                                                        const updatedForms = [...guardianForms];
                                                        updatedForms[index].name = e.target.value;
                                                        setGuardianForms(updatedForms);
                                                    }
                                                }}
                                                disabled={guardian.disabled}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={guardian.relation}
                                                onChange={(e) => {
                                                    if (!guardian.disabled) {
                                                        const updatedForms = [...guardianForms];
                                                        updatedForms[index].relation = e.target.value;
                                                        setGuardianForms(updatedForms);
                                                    }
                                                }}
                                                disabled={guardian.disabled}
                                            />
                                        </td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3">No guardians available.</td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            </div>

            <div className="sg-submit-btn-container">
                <button className="submit-button" onClick={handleSubmit}>
                    Submit
                </button>
            </div>
        </div>
    );
};

export default NewPlayerGuardian;
